import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Checkbox,
  Form,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Segment,
} from "semantic-ui-react";

import {
  doc,
  getDoc,
} from "firebase/firestore";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import Alerta from "./Alertas";

import {
  auth,
  db,
} from "../firebase/firebaseConfig";

import useObtenerUsuarioAlertas from "../hooks/useObtenerUsuarioAlertas";
import ActualizaUsuariosAlertasDb from "../firebase/ActualizarUsuariosAlertasDb";


// ======================================================
// COLORES / ESTILOS
// ======================================================

const COLORS = {
  background: "#f8fafc",
  card: "#ffffff",
  border: "#e5e9ef",

  title: "#172b4d",
  text: "#344563",
  muted: "#6b778c",

  teal: "#0d9da5",
  tealSoft: "#e9f7f8",

  blue: "#2185d0",
  blueSoft: "#eef6fc",

  green: "#21ba45",
  greenSoft: "#eefaf1",

  orange: "#f2711c",
  orangeSoft: "#fff4ec",
};


const cardStyle = {
  margin: 0,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  background: COLORS.card,
  boxShadow: "0 1px 3px rgba(9, 30, 66, 0.08)",
};


// ======================================================
// COMPONENTE
// ======================================================

export const ActulizaUsuariosAlertas = () => {

  const navigate =
    useNavigate();

  const { id } =
    useParams();

  const [
    usuariosAlertas,
  ] =
    useObtenerUsuarioAlertas(
      id
    );


  // ====================================================
  // USUARIO ACTUAL
  // ====================================================

  const userAuthEmail =
    (
      auth?.currentUser?.email ||
      ""
    )
      .trim()
      .toLowerCase();


  // ====================================================
  // IDENTIDAD
  // ====================================================

  const [
    correo,
    setCorreo,
  ] = useState("");

  const [
    nombre,
    setNombre,
  ] = useState("");

  const [
    entidad,
    setEntidad,
  ] = useState("");


  // ====================================================
  // ALERTAS
  //
  // Solo se mantienen las categorías que actualmente
  // tienen una funcionalidad real en LogisticMed.
  // ====================================================

  const [
    salida,
    setSalida,
  ] = useState(false);

  const [
    mantencion,
    setMantencion,
  ] = useState(false);


  // ====================================================
  // UI
  // ====================================================

  const [
    alerta,
    cambiarAlerta,
  ] = useState({});

  const [
    estadoAlerta,
    cambiarEstadoAlerta,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    isChecking,
    setIsChecking,
  ] = useState(false);


  // ====================================================
  // ORIGINAL
  //
  // Solo se comparan las dos alertas vigentes.
  //
  // Los documentos antiguos pueden seguir teniendo
  // rfid, confirma o tecnico. Esos campos se ignoran.
  // ====================================================

  const original =
    useMemo(
      () => {

        if (
          !usuariosAlertas
        ) {
          return null;
        }


        return {

          salida:
            Boolean(
              usuariosAlertas.salida
            ),

          mantencion:
            Boolean(
              usuariosAlertas.mantencion
            ),

        };

      },
      [
        usuariosAlertas,
      ]
    );


  // ====================================================
  // CAMBIOS
  // ====================================================

  const hasChanges =
    useMemo(
      () => {

        if (!original) {
          return false;
        }


        return (
          salida !==
            original.salida ||

          mantencion !==
            original.mantencion
        );

      },
      [
        original,
        salida,
        mantencion,
      ]
    );


  // ====================================================
  // TIENE AL MENOS UNA ALERTA
  // ====================================================

  const tieneAlertasSeleccionadas =
    useMemo(
      () =>
        salida ||
        mantencion,
      [
        salida,
        mantencion,
      ]
    );


  // ====================================================
  // CARGAR REGISTRO EN FORMULARIO
  // ====================================================

  useEffect(() => {

    if (
      usuariosAlertas
    ) {

      setNombre(
        usuariosAlertas.nombre ||
        ""
      );

      setCorreo(
        usuariosAlertas.correo ||
        ""
      );

      setEntidad(
        usuariosAlertas.empresa ||
        ""
      );


      setSalida(
        Boolean(
          usuariosAlertas.salida
        )
      );


      setMantencion(
        Boolean(
          usuariosAlertas.mantencion
        )
      );


      return;

    }


    if (
      usuariosAlertas === null
    ) {

      navigate(
        "/configuracion/envios"
      );

    }

  }, [
    usuariosAlertas,
    navigate,
  ]);


  // ====================================================
  // MOSTRAR ALERTA
  // ====================================================

  const mostrarAlerta =
    (
      tipo,
      mensaje
    ) => {

      cambiarAlerta({
        tipo,
        mensaje,
      });

      cambiarEstadoAlerta(true);

    };


  // ====================================================
  // VOLVER
  // ====================================================

  const volver =
    () => {

      navigate(
        "/configuracion/envios"
      );

    };


  // ====================================================
  // GUARDAR
  // ====================================================

  const handleSubmit =
    async () => {

      cambiarEstadoAlerta(false);
      cambiarAlerta({});


      if (!id) {

        mostrarAlerta(
          "error",
          "No se pudo identificar la configuración de alertas."
        );

        return;

      }


      if (!hasChanges) {

        mostrarAlerta(
          "warning",
          "No se detectaron cambios para guardar."
        );

        return;

      }


      if (
        !tieneAlertasSeleccionadas
      ) {

        mostrarAlerta(
          "warning",
          "Debe mantener al menos una alerta activa."
        );

        return;

      }


      setIsSaving(true);
      setIsChecking(true);


      try {

        // ============================================
        // VALIDACIÓN FRESCA DEL DOCUMENTO
        // ============================================

        const alertaRef =
          doc(
            db,
            "usuariosalertas",
            id
          );


        const snapAlerta =
          await getDoc(
            alertaRef
          );


        if (
          !snapAlerta.exists()
        ) {

          mostrarAlerta(
            "error",
            "La configuración de alertas ya no existe."
          );


          setTimeout(
            () => {

              navigate(
                "/configuracion/envios"
              );

            },
            1200
          );


          return;

        }


        setIsChecking(false);


        // ============================================
        // ACTUALIZAR
        //
        // Solo se actualizan las dos categorías
        // actualmente vigentes:
        //
        // salida
        // mantencion
        // ============================================

        await ActualizaUsuariosAlertasDb({

          id,

          salida:
            Boolean(salida),

          mantencion:
            Boolean(mantencion),

          userMod:
            userAuthEmail,

          fechaMod:
            new Date(),

        });


        mostrarAlerta(
          "exito",
          "Configuración de alertas actualizada correctamente."
        );


        setTimeout(
          () => {

            navigate(
              "/configuracion/envios"
            );

          },
          900
        );

      } catch (error) {

        console.error(
          "Error actualizando configuración de alertas:",
          error
        );


        mostrarAlerta(
          "error",
          "Se produjo un error al actualizar. Intente nuevamente."
        );

      } finally {

        setIsSaving(false);
        setIsChecking(false);

      }

    };


  // ====================================================
  // LOADING INICIAL
  // ====================================================

  if (
    usuariosAlertas === undefined
  ) {

    return (

      <div
        style={{
          minHeight: "100vh",

          background:
            COLORS.background,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >

        <Loader
          active
          inline="centered"
        >
          Cargando configuración...
        </Loader>

      </div>

    );

  }


  // ====================================================
  // RETURN
  // ====================================================

  return (

    <div
      style={{
        minHeight: "100vh",

        background:
          COLORS.background,

        padding:
          "24px 18px 40px",
      }}
    >

      <div
        style={{
          width: "100%",
          maxWidth: 1000,
          margin: "0 auto",
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <div
          style={{
            display: "flex",
            alignItems: "center",

            justifyContent:
              "space-between",

            gap: 16,
            flexWrap: "wrap",

            marginBottom: 20,
          }}
        >

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >

            <div
              style={{
                width: 48,
                height: 48,

                borderRadius: 9,

                background:
                  COLORS.tealSoft,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                flexShrink: 0,
              }}
            >

              <Icon
                name="bell outline"
                size="large"

                style={{
                  color:
                    COLORS.teal,

                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  color:
                    COLORS.title,

                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                Actualizar Alertas
              </div>


              <div
                style={{
                  color:
                    COLORS.muted,

                  fontSize: 13,
                  marginTop: 4,
                }}
              >
                Modifique las notificaciones que recibirá el usuario
              </div>

            </div>

          </div>


          <Button
            basic
            size="small"

            disabled={
              isSaving
            }

            onClick={
              volver
            }
          >
            <Icon name="arrow left" />
            Volver
          </Button>

        </div>


        {/* =================================================
            INFORMACIÓN DEL USUARIO
        ================================================= */}

        <Segment
          style={{
            ...cardStyle,
            padding: 0,
            marginBottom: 18,
          }}
        >

          <div
            style={{
              padding:
                "14px 18px",

              borderBottom:
                `1px solid ${COLORS.border}`,

              display: "flex",
              alignItems: "center",
              gap: 9,
            }}
          >

            <Icon
              name="user outline"

              style={{
                color:
                  COLORS.teal,

                margin: 0,
              }}
            />


            <div>

              <div
                style={{
                  color:
                    COLORS.title,

                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                Usuario
              </div>

              <div
                style={{
                  color:
                    COLORS.muted,

                  fontSize: 11,
                  marginTop: 2,
                }}
              >
                Datos asociados a esta configuración
              </div>

            </div>

          </div>


          <div
            style={{
              padding:
                "18px",
            }}
          >

            <Form>

              <Form.Group
                widths="equal"
              >

                <Form.Field>

                  <label>
                    Correo
                  </label>

                  <Input
                    fluid
                    readOnly

                    value={
                      correo
                    }

                    placeholder="Correo"

                    icon={{
                      name:
                        "mail outline",

                      color:
                        "blue",
                    }}

                    iconPosition="left"
                  />

                </Form.Field>


                <Form.Field>

                  <label>
                    Nombre
                  </label>

                  <Input
                    fluid
                    readOnly

                    value={
                      nombre
                    }

                    placeholder="Nombre"

                    icon={{
                      name:
                        "user outline",

                      color:
                        "blue",
                    }}

                    iconPosition="left"
                  />

                </Form.Field>


                <Form.Field>

                  <label>
                    Empresa
                  </label>

                  <Input
                    fluid
                    readOnly

                    value={
                      entidad
                    }

                    placeholder="Empresa"

                    icon={{
                      name:
                        "building outline",

                      color:
                        "blue",
                    }}

                    iconPosition="left"
                  />

                </Form.Field>

              </Form.Group>

            </Form>

          </div>

        </Segment>


        {/* =================================================
            ALERTAS
        ================================================= */}

        <Segment
          style={{
            ...cardStyle,
            padding: 0,
          }}
        >

          <div
            style={{
              padding:
                "14px 18px",

              borderBottom:
                `1px solid ${COLORS.border}`,

              display: "flex",
              alignItems: "center",

              justifyContent:
                "space-between",

              gap: 12,
              flexWrap: "wrap",
            }}
          >

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
              }}
            >

              <Icon
                name="settings"

                style={{
                  color:
                    COLORS.teal,

                  margin: 0,
                }}
              />


              <div>

                <div
                  style={{
                    color:
                      COLORS.title,

                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  Alertas que recibirá
                </div>

                <div
                  style={{
                    color:
                      COLORS.muted,

                    fontSize: 11,
                    marginTop: 2,
                  }}
                >
                  Active o desactive las categorías de notificación
                </div>

              </div>

            </div>


            {hasChanges && (

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,

                  padding:
                    "5px 9px",

                  borderRadius: 999,

                  background:
                    COLORS.blueSoft,

                  color:
                    COLORS.blue,

                  fontSize: 11,
                  fontWeight: 700,
                }}
              >

                <Icon
                  name="circle"
                  fitted
                  size="tiny"
                />

                Cambios sin guardar

              </div>

            )}

          </div>


          <div
            style={{
              padding:
                "18px",
            }}
          >

            <div
              style={{
                display: "grid",

                gridTemplateColumns:
                  "repeat(auto-fit, minmax(260px, 1fr))",

                gap: 10,
              }}
            >

              {/* ===========================================
                  SALIDAS Y TRASPASOS
              =========================================== */}

              <AlertOption
                icon="exchange"

                label="Salidas y traspasos"

                description="Notificaciones de movimientos y traslados de equipos"

                color={
                  COLORS.orange
                }

                background={
                  COLORS.orangeSoft
                }

                checked={
                  salida
                }

                disabled={
                  isSaving
                }

                onChange={
                  setSalida
                }
              />


              {/* ===========================================
                  MANTENCIONES
              =========================================== */}

              <AlertOption
                icon="calendar check outline"

                label="Mantenciones"

                description="Mantenciones próximas a vencer y vencidas"

                color={
                  COLORS.teal
                }

                background={
                  COLORS.tealSoft
                }

                checked={
                  mantencion
                }

                disabled={
                  isSaving
                }

                onChange={
                  setMantencion
                }
              />

            </div>


            {!tieneAlertasSeleccionadas && (

              <Message
                warning
                size="small"

                style={{
                  marginTop: 16,
                }}
              >

                <Icon name="warning circle" />

                Debe mantener al menos una categoría de alerta activa.

              </Message>

            )}


            <div
              style={{
                display: "flex",

                justifyContent:
                  "flex-end",

                gap: 8,
                flexWrap: "wrap",

                marginTop: 20,
              }}
            >

              <Button
                basic
                type="button"

                onClick={
                  volver
                }

                disabled={
                  isSaving
                }
              >
                <Icon name="arrow left" />
                Volver
              </Button>


              <Button
                color="teal"
                type="button"

                onClick={
                  handleSubmit
                }

                loading={
                  isSaving
                }

                disabled={
                  isSaving ||
                  !hasChanges ||
                  !tieneAlertasSeleccionadas
                }

                title={
                  !hasChanges
                    ? "No hay cambios para guardar"
                    : !tieneAlertasSeleccionadas
                    ? "Debe mantener al menos una alerta"
                    : "Guardar cambios"
                }
              >

                <Icon name="save" />

                {isChecking
                  ? "Verificando..."
                  : "Actualizar"}

              </Button>

            </div>

          </div>

        </Segment>


        {/* =================================================
            ALERTA
        ================================================= */}

        <Alerta
          tipo={
            alerta.tipo
          }

          mensaje={
            alerta.mensaje
          }

          estadoAlerta={
            estadoAlerta
          }

          cambiarEstadoAlerta={
            cambiarEstadoAlerta
          }
        />

      </div>

    </div>

  );

};


// ======================================================
// OPCIÓN DE ALERTA
// ======================================================

const AlertOption = ({
  icon,
  label,
  description,
  color,
  background,
  checked,
  disabled,
  onChange,
}) => {

  return (

    <div
      style={{
        minHeight: 76,

        padding:
          "12px 13px",

        border:
          `1px solid ${
            checked
              ? color
              : COLORS.border
          }`,

        background:
          checked
            ? background
            : "#ffffff",

        borderRadius: 7,

        display: "flex",
        alignItems: "center",
        gap: 11,

        opacity:
          disabled
            ? 0.65
            : 1,

        transition:
          "all 0.15s ease",
      }}
    >

      <div
        style={{
          width: 34,
          height: 34,

          borderRadius: 7,

          display: "flex",
          alignItems: "center",

          justifyContent:
            "center",

          background,

          flexShrink: 0,
        }}
      >

        <Icon
          name={icon}

          style={{
            color,
            margin: 0,
          }}
        />

      </div>


      <div
        style={{
          flex: 1,
          minWidth: 0,
        }}
      >

        <div
          style={{
            color:
              COLORS.title,

            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {label}
        </div>


        <div
          style={{
            color:
              COLORS.muted,

            fontSize: 10,
            marginTop: 2,
          }}
        >
          {description}
        </div>

      </div>


      <Checkbox
        toggle

        checked={
          Boolean(
            checked
          )
        }

        disabled={
          disabled
        }

        onChange={(
          e,
          data
        ) =>
          onChange(
            Boolean(
              data.checked
            )
          )
        }
      />

    </div>

  );

};


export default ActulizaUsuariosAlertas;